import {
  Archive, ArrowLeft, History as HistoryIcon, MoreHorizontal, PauseCircle, Redo2, RotateCcw, Send, Undo2,
} from "lucide-react";
import * as React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Page } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { Tabs, TabsContent, TabsList, TabsTrigger, Tooltip } from "@/components/ui/misc";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/components/ui/toast";
import { TextPicker, Token } from "@/features/cadence-tokens";
import { WorkflowBuilder, WorkflowSettingsCard, type BuilderProblems } from "@/features/workflow-builder";
import { WorkflowLeads } from "@/features/workflow-leads";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { sameValues } from "@/lib/form";
import { useLeaveGuard } from "@/lib/leave-guard";
import { useHistory } from "@/lib/use-history";
import { cn, formatDateTime } from "@/lib/utils";
import {
  boardCounts, placeFromParams, placeText, problemFrom, problemOnCard, problemPlace, publishProblem, publishSummary,
  statusBadge, withPlace, workflowPayload,
  type BoardPlace, type WorkflowBody, type WorkflowDetail,
} from "@/lib/workflows";
import {
  useSaveWorkflow, useTenantWorkflowsPaused, useWorkflow, useWorkflowAction, useWorkflowCatalog, useWorkflowStepCounts,
} from "@/lib/workflows-api";

/**
 * One workflow (ADR-111 in platform, ACL-367): its builder and the leads it has had. What is on
 * the page is the draft. Saving keeps the draft and changes nothing for any lead; publishing makes
 * the draft the version new leads start on, after a confirmation that says in words what it will
 * do. A lead already in it finishes the version it began on.
 */
export function WorkflowPage() {
  const { workflowId } = useParams();
  const { data, isPending, error, refetch } = useWorkflow(workflowId);
  if (isPending) {
    return (
      <Page>
        <div className="grid place-items-center py-24">
          <Spinner />
        </div>
      </Page>
    );
  }
  // Only a first read that failed replaces the page: a background refetch that fails keeps the
  // workflow it already has, and the edits on it.
  if (error && !data) {
    return (
      <Page>
        {error instanceof ApiError && error.status === 404 ? (
          <EmptyState
            title="No such workflow"
            description="It may belong to another organisation."
            action={
              <Button variant="outline" asChild>
                <Link to="/workflows">All workflows</Link>
              </Button>
            }
          />
        ) : (
          <ErrorState
            error={error}
            title="Could not load the workflow"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        )}
      </Page>
    );
  }
  if (!data) return null;
  return <Editor key={data.id} workflow={data} />;
}

interface Values {
  name: string;
  folder: string;
  body: WorkflowBody;
}

type Problem = { message: string; stepId: string | null };

const valuesOf = (w: WorkflowDetail): Values => ({ name: w.name, folder: w.folder ?? "", body: w.draft_body });

/** The key that goes with Z and S in a shortcut, as this computer writes it. */
const MOD = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl+";

const STALE = "Somebody else saved this workflow";

function Editor({ workflow }: { workflow: WorkflowDetail }) {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "leads" ? "leads" : params.get("tab") === "settings" ? "settings" : "builder";
  const setTab = React.useCallback(
    (next: string) => {
      // The leads a count stood for go with the tab they were shown on.
      const p = withPlace(params, null);
      if (next === "builder") p.delete("tab");
      else p.set("tab", next);
      setParams(p, { replace: true });
    },
    [params, setParams],
  );
  // Enrollment history at the leads one count on the board stands for (ACL-407).
  const boardPlace = tab === "leads" ? placeFromParams(params) : null;
  const showLeads = React.useCallback(
    (at: BoardPlace | null) => {
      const p = withPlace(params, at);
      p.set("tab", "leads");
      setParams(p, { replace: true });
    },
    [params, setParams],
  );
  const { user } = useAuth();
  // A superadmin may be in another tenant's workflow: the builder offers that tenant's outcomes.
  const catalog = useWorkflowCatalog(user?.role === "superadmin" ? workflow.tenant_id : undefined);
  const paused = useTenantWorkflowsPaused(workflow.tenant_id);
  const reload = useWorkflow(workflow.id);
  const save = useSaveWorkflow();
  const action = useWorkflowAction();

  const [baseline, setBaseline] = React.useState<Values>(() => valuesOf(workflow));
  // The `updated_at` the draft on the page was read at: a save over a newer one is refused.
  const [readAt, setReadAt] = React.useState(workflow.updated_at);
  const history = useHistory<Values>(baseline);
  const { undo: undoStep, redo: redoStep, reset } = history;
  const values = history.value;
  const valuesRef = React.useRef(values);
  valuesRef.current = values;
  const [problem, setProblem] = React.useState<Problem | null>(null);
  // Undo and redo are edits like any other: the server's last answer was about what was on the
  // page before them.
  const undo = React.useCallback(() => {
    setProblem(null);
    undoStep();
  }, [undoStep]);
  const redo = React.useCallback(() => {
    setProblem(null);
    redoStep();
  }, [redoStep]);
  const [showProblems, setShowProblems] = React.useState(false);
  const [stale, setStale] = React.useState(false);
  const [publishing, setPublishing] = React.useState(false);
  const [unpublishing, setUnpublishing] = React.useState(false);
  const [archiving, setArchiving] = React.useState(false);

  const readOnly = workflow.status === "archived";
  // The board's counts are the published workflow's: a draft has run nobody.
  const live = workflow.status === "published";
  const stepCounts = useWorkflowStepCounts(workflow.id, live);
  const counted = React.useMemo(
    () => (live && stepCounts.data ? boardCounts(stepCounts.data, values.body) : null),
    [live, stepCounts.data, values.body],
  );
  const dirty = !sameValues(values, baseline);
  const busy = save.isPending || action.isPending;
  const local = publishProblem(values.body, catalog.data);
  const badge = statusBadge({ status: workflow.status, has_unpublished_changes: workflow.has_unpublished_changes || dirty });

  // What each box says is wrong: the server's last refusal (it names the step), else the page's own
  // copy of the publish check once somebody has tried to publish.
  const shown = problem ?? (showProblems ? local : null);
  const placeOf = (p: Problem) => (p.stepId ? { stepId: p.stepId } : problemPlace(p.message));
  const place = shown ? placeOf(shown) : null;
  const problems: BuilderProblems = {
    start: shown && place === "start" ? shown.message : undefined,
    steps: shown && place && typeof place === "object" ? { [place.stepId]: problemOnCard(shown.message) } : {},
  };
  const pageProblem = shown && place === "page" ? shown.message : null;

  /** A problem about a box is shown on the Builder tab, so go there. */
  const reveal = React.useCallback(
    (p: Problem) => {
      const at = p.stepId ? "step" : problemPlace(p.message);
      if (at !== "page") setTab("builder");
    },
    [setTab],
  );

  // Another admin's save while this page is open is taken in when nothing here is unsaved.
  React.useEffect(() => {
    if (dirty) return;
    setReadAt(workflow.updated_at);
    const remote = valuesOf(workflow);
    if (!sameValues(remote, baseline)) {
      setBaseline(remote);
      reset(remote);
    }
  }, [workflow, dirty, baseline, reset]);

  const set = (fn: (body: WorkflowBody) => WorkflowBody, key?: string) => {
    setProblem(null);
    history.set((v) => ({ ...v, body: fn(v.body) }), key);
  };
  const setField = (field: "name" | "folder", value: string) => {
    setProblem(null);
    history.set((v) => ({ ...v, [field]: value }), field);
  };

  const keep = React.useCallback(async (): Promise<WorkflowDetail | null> => {
    setProblem(null);
    const sent = valuesRef.current;
    if (!sent.name.trim()) {
      setProblem({ message: "Give the workflow a name.", stepId: null });
      return null;
    }
    try {
      const saved = await save.mutateAsync({
        id: workflow.id,
        ...workflowPayload(sent.name, sent.folder, sent.body, readAt),
      });
      const kept = valuesOf(saved);
      setBaseline(kept);
      setReadAt(saved.updated_at);
      // Anything typed while the save was on its way stays on the page, unsaved, on top of it.
      if (sameValues(valuesRef.current, sent)) reset(kept);
      return saved;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.message.startsWith(STALE)) {
        setStale(true);
        return null;
      }
      const p = problemFrom(e, "Could not save the workflow. Please try again.");
      setProblem(p);
      reveal(p);
      return null;
    }
  }, [save, workflow.id, readAt, reset, reveal]);

  const onSave = React.useCallback(async () => {
    if (readOnly || busy || !dirty) return;
    const saved = await keep();
    if (saved) {
      toast.success(
        "Draft saved",
        saved.status === "published"
          ? "Leads keep getting the published version until you publish this."
          : "Nothing happens to any lead until you publish it.",
      );
    }
  }, [readOnly, busy, dirty, keep, toast]);

  const askToPublish = () => {
    setShowProblems(true);
    setProblem(null);
    if (local) {
      reveal(local);
      return;
    }
    setPublishing(true);
  };

  const publish = async () => {
    // The draft as this page read it, or as this click just saved it: somebody else's newer save is
    // refused by the server rather than published unseen.
    let at = readAt;
    if (dirty) {
      const saved = await keep();
      if (!saved) {
        setPublishing(false);
        return;
      }
      at = saved.updated_at;
    }
    try {
      const done = await action.mutateAsync({ id: workflow.id, action: "publish", updatedAt: at });
      setReadAt(done.updated_at);
      toast.success(`Published as version ${done.published_version_n ?? 1}`, "New leads start on it from now on.");
      setShowProblems(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.message.startsWith(STALE)) {
        setStale(true);
        return;
      }
      const p = problemFrom(e, "Could not publish. Please try again.");
      setProblem(p);
      reveal(p);
    } finally {
      setPublishing(false);
    }
  };

  const act = async (verb: "unpublish" | "archive" | "restore") => {
    try {
      const done = await action.mutateAsync({ id: workflow.id, action: verb });
      setReadAt(done.updated_at);
      if (verb === "archive") {
        // An archived workflow cannot be changed: what was not saved goes with the archiving.
        const kept = valuesOf(done);
        setBaseline(kept);
        reset(kept);
      }
      toast.success(verb === "unpublish" ? "Unpublished" : verb === "archive" ? "Archived" : "Restored as a draft", workflow.name);
      setUnpublishing(false);
      setArchiving(false);
    } catch (e) {
      toast.error("Could not change the workflow", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  const loadTheirs = async () => {
    const fresh = await reload.refetch();
    // A refetch that failed still hands back the copy cached before: that is not their version.
    if (!fresh.isSuccess) {
      toast.error("Could not load their version", "Please try again.");
      return;
    }
    const theirs = valuesOf(fresh.data);
    setBaseline(theirs);
    setReadAt(fresh.data.updated_at);
    reset(theirs);
    setStale(false);
    setProblem(null);
  };

  // ⌘Z / ⌘⇧Z to undo and redo outside a text field, on the tabs that edit; ⌘S to save anywhere.
  React.useEffect(() => {
    if (readOnly) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      if (key === "s") {
        e.preventDefault();
        void onSave();
        return;
      }
      if (tab === "leads") return;
      const typing = e.target instanceof HTMLElement && e.target.closest("input, textarea, [contenteditable=true]") !== null;
      if (typing) return;
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [readOnly, onSave, undo, redo, tab]);

  // Leaving with unsaved changes asks first, however the page is left (ACL-404).
  const { dialog: leaveDialog } = useLeaveGuard(dirty && !readOnly, "Your changes to this workflow are not saved.");

  return (
    <Page className="max-w-none pb-0">
      <Link to="/workflows" className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text">
        <ArrowLeft className="size-3.5" aria-hidden />
        Workflows
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 pb-4">
        <div className="min-w-[16rem] flex-1 space-y-1.5">
          {readOnly ? (
            <h1 className="text-xl font-semibold tracking-tight text-text">{values.name}</h1>
          ) : (
            <input
              aria-label="Workflow name"
              placeholder="Name this workflow"
              value={values.name}
              maxLength={120}
              onChange={(e) => setField("name", e.target.value)}
              className={cn(
                "-ml-1.5 w-full max-w-xl rounded-md border border-transparent bg-transparent px-1.5 py-0.5 text-xl font-semibold tracking-tight text-text",
                "placeholder:text-text-subtle hover:border-border focus-visible:border-accent-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/25",
              )}
            />
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
            <Badge tone={badge.tone} size="sm">
              {badge.label}
            </Badge>
            <Token
              label="Folder"
              readOnly={readOnly}
              tone="muted"
              title="Folder"
              hint="Workflows in one folder are listed together. Leave it empty for none."
              content={(close) => (
                <TextPicker
                  label="Folder"
                  value={values.folder}
                  validate={(v) => (v.trim().length > 80 ? "At most 80 characters" : null)}
                  onChange={(folder) => setField("folder", folder)}
                  close={close}
                />
              )}
            >
              {values.folder ? `Folder: ${values.folder}` : "No folder"}
            </Token>
            {workflow.counts.enrolled > 0 && (
              <span>
                {workflow.counts.active} in it now · {workflow.counts.enrolled} entered in all
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {!readOnly && tab !== "leads" && (
            <>
              <Tooltip content={`Undo (${MOD}Z)`}>
                <Button variant="ghost" size="icon-sm" aria-label="Undo" disabled={!history.canUndo} onClick={undo}>
                  <Undo2 />
                </Button>
              </Tooltip>
              <Tooltip content={`Redo (${MOD}Shift+Z)`}>
                <Button variant="ghost" size="icon-sm" aria-label="Redo" disabled={!history.canRedo} onClick={redo}>
                  <Redo2 />
                </Button>
              </Tooltip>
            </>
          )}
          {workflow.versions.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm">
                  <HistoryIcon />
                  {workflow.versions.length} version{workflow.versions.length === 1 ? "" : "s"}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-2">
                <ol className="max-h-80 space-y-1 overflow-y-auto">
                  {workflow.versions.map((v) => (
                    <li key={v.version_n} className="rounded-md px-2 py-1.5 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-text">
                          Version {v.version_n}
                          {v.version_n === workflow.published_version_n && workflow.status === "published" && " · live"}
                        </span>
                        <span className="text-text-subtle">{formatDateTime(v.published_at)}</span>
                      </div>
                      {v.published_by_name && <p className="text-text-muted">by {v.published_by_name}</p>}
                    </li>
                  ))}
                </ol>
              </PopoverContent>
            </Popover>
          )}
          {!readOnly && (
            <Tooltip content={`Save the draft (${MOD}S)`}>
              <Button variant="outline" size="sm" disabled={!dirty || busy} loading={save.isPending} onClick={() => void onSave()}>
                Save
              </Button>
            </Tooltip>
          )}
          {!readOnly && (workflow.status !== "published" || workflow.has_unpublished_changes || dirty) && (
            <Button variant="default" size="sm" disabled={busy} onClick={askToPublish}>
              <Send />
              {workflow.status === "published" ? "Publish changes" : "Publish"}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="More">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {workflow.status === "published" && (
                <DropdownMenuItem onSelect={() => setUnpublishing(true)}>
                  <PauseCircle />
                  Unpublish
                </DropdownMenuItem>
              )}
              {workflow.status === "draft" && (
                <DropdownMenuItem onSelect={() => (dirty ? setArchiving(true) : void act("archive"))}>
                  <Archive />
                  Archive
                </DropdownMenuItem>
              )}
              {workflow.status === "archived" && (
                <DropdownMenuItem onSelect={() => void act("restore")}>
                  <RotateCcw />
                  Restore as a draft
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="space-y-3 pb-4 empty:hidden">
        {paused.data?.paused && (
          <Callout tone="warning">
            Every workflow is paused, so this one does nothing right now. It can be turned back on from{" "}
            <Link to="/workflows?tab=settings" className="font-medium underline underline-offset-4">
              Workflows, Settings
            </Link>
            .
          </Callout>
        )}
        {stale && (
          <Callout tone="warning">
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              Somebody else saved this workflow after you opened it, so yours was not saved or published.
              <Button variant="outline" size="sm" loading={reload.isFetching} onClick={() => void loadTheirs()}>
                Load their version
              </Button>
              <span className="text-text-muted">Your unsaved changes here will be replaced.</span>
            </span>
          </Callout>
        )}
        {readOnly && (
          <Callout tone="warning">Archived. It does nothing and cannot be changed. Restore it as a draft to work on it again.</Callout>
        )}
        {workflow.status === "published" && (workflow.has_unpublished_changes || dirty) && (
          <Callout tone="info">
            Leads are getting version {workflow.published_version_n}. What you see here reaches them once you publish the
            changes.
          </Callout>
        )}
        {pageProblem && <Callout tone="danger">{pageProblem}</Callout>}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-0">
          <TabsTrigger value="builder">Builder</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="leads">
            Enrollment history{workflow.counts.enrolled > 0 && ` (${workflow.counts.enrolled})`}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="builder">
          {catalog.isPending ? (
            <div className="-mx-6 grid min-h-[320px] place-items-center border-y border-border bg-surface-sunken">
              <Spinner />
            </div>
          ) : catalog.error ? (
            <div className="pt-6">
              <ErrorState
                error={catalog.error}
                title="Could not load the builder's choices"
                action={
                  <Button variant="outline" onClick={() => void catalog.refetch()}>
                    Try again
                  </Button>
                }
              />
            </div>
          ) : (
            <WorkflowBuilder
              body={values.body}
              set={set}
              catalog={catalog.data}
              readOnly={readOnly}
              problems={problems}
              counts={counted}
              onShowLeads={showLeads}
              onRefreshCounts={live ? () => void stepCounts.refetch() : undefined}
              refreshingCounts={stepCounts.isFetching}
              countsFailed={Boolean(stepCounts.error)}
              highlight={params.get("step")}
            />
          )}
        </TabsContent>
        <TabsContent value="settings" className="pt-6">
          <WorkflowSettingsCard body={values.body} set={set} readOnly={readOnly} />
        </TabsContent>
        <TabsContent value="leads" className="pt-6">
          <WorkflowLeads
            workflow={workflow}
            catalog={catalog.data}
            onShowOnBoard={(stepId) => {
              const p = new URLSearchParams(params);
              p.delete("tab");
              p.set("step", stepId);
              setParams(p, { replace: true });
            }}
            place={boardPlace}
            placeName={boardPlace ? placeText(boardPlace, [workflow.published_body, values.body], catalog.data) : null}
            onClearPlace={() => setParams(withPlace(params, null), { replace: true })}
          />
        </TabsContent>
      </Tabs>

      {!readOnly && dirty ? (
        <div
          className={cn(
            "sticky bottom-0 z-20 -mx-6 border-t border-border bg-surface/95 px-6 py-3 backdrop-blur",
            tab !== "builder" && "mt-8",
          )}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex min-w-0 flex-1 items-center gap-1.5 text-xs text-text-muted">
              <span className="size-1.5 rounded-full bg-warning-9" aria-hidden />
              Unsaved changes. Save keeps the draft; no lead gets anything until you publish.
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                reset(baseline);
                setProblem(null);
              }}
            >
              Discard changes
            </Button>
          </div>
        </div>
      ) : (
        tab !== "builder" && <div className="h-8" />
      )}

      <ConfirmDialog
        open={publishing}
        onOpenChange={setPublishing}
        title={workflow.status === "published" ? "Publish these changes?" : `Publish ${values.name.trim() || "this workflow"}?`}
        description={
          <span className="space-y-2">
            <span className="block">{publishSummary(values.body, catalog.data)}</span>
            {workflow.status === "published" && workflow.counts.active > 0 && (
              <span className="block text-text-muted">
                The {workflow.counts.active === 1 ? "lead" : `${workflow.counts.active} leads`} already in it finish the
                version they started on.
              </span>
            )}
          </span>
        }
        confirmLabel={dirty ? "Save and publish" : "Publish"}
        loading={busy}
        onConfirm={() => void publish()}
      />

      <ConfirmDialog
        open={unpublishing}
        onOpenChange={setUnpublishing}
        title={`Unpublish ${workflow.name}?`}
        description={
          workflow.counts.active
            ? `No new lead will start it, and the ${workflow.counts.active === 1 ? "lead" : `${workflow.counts.active} leads`} in it now leave it at once: they get no more texts from it.`
            : "No new lead will start it. You can publish it again later."
        }
        confirmLabel="Unpublish"
        loading={action.isPending}
        onConfirm={() => void act("unpublish")}
      />

      <ConfirmDialog
        open={archiving}
        onOpenChange={setArchiving}
        title="Archive and drop your unsaved changes?"
        description="An archived workflow cannot be changed, so what you have not saved is not kept. Save first to keep it."
        confirmLabel="Archive"
        destructive
        loading={action.isPending}
        onConfirm={() => void act("archive")}
      />

      {leaveDialog}
    </Page>
  );
}
